import React, { useState, useEffect } from "react";
import {
  Layout,
  Button,
  Table,
  Space,
  Modal,
  Input,
  Form,
  Card,
  Typography,
  Popconfirm,
  Upload,
  Switch,
  message,
  Image,
} from "antd";

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DragOutlined,
} from "@ant-design/icons";

import "./machineries.css";
import ImageGridSorter from "./ImageGridSorter";

import {
  createMachinery,
  deleteMachinery,
  getMachineries,
  updateMachinery,
  updateMachineryImage,
  updateMachineryPriority,
  updatePublishStatus,
} from "../services/machinery.service";

const { Content } = Layout;
const { Title } = Typography;

const MACHINE_DETAIL_FIELDS = [
  { name: "key1Title", label: "Key 1 Title" },
  { name: "key1Value", label: "Key 1 Value" },
  { name: "key2Title", label: "Key 2 Title" },
  { name: "key2Value", label: "Key 2 Value" },
  { name: "key3Title", label: "Key 3 Title" },
  { name: "key3Value", label: "Key 3 Value" },
];

const buildMachinePayload = (values) => ({
  machineTitle: values.machineTitle,
  machineSubTitle: values.machineSubTitle,
  key1Title: values.key1Title,
  key1Value: values.key1Value,
  key2Title: values.key2Title,
  key2Value: values.key2Value,
  key3Title: values.key3Title,
  key3Value: values.key3Value,
});

const Machineries = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);

  const [imageBase64, setImageBase64] = useState("");
  const [fileList, setFileList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const pageSize = 5;

  const [form] = Form.useForm();

  const [isOrderView, setIsOrderView] = useState(false);
  const [orderedData, setOrderedData] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
  // Fetch

  const fetchMachines = async (page = 1) => {
    try {
      setLoading(true);

      const res = await getMachineries(page, pageSize);

      setMachines(res.data.data);
      setTotalItems(res.data.pagination.totalItems);
      setCurrentPage(page);
    } catch {
      message.error("Failed to load machinery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMachines(currentPage);
  }, [currentPage]);

  // Submit

  const handleSubmit = async (values) => {
    try {
      const machinePayload = buildMachinePayload(values);

      if (editingMachine) {
        await updateMachinery(editingMachine.id, machinePayload);

        message.success("Machine updated");
      } else {
        await createMachinery({
          ...machinePayload,
          machineImage: imageBase64,
          isPublish: values.isPublish,
        });

        message.success("Machine added");
      }

      setIsModalVisible(false);
      setEditingMachine(null);
      form.resetFields();
      fetchMachines(currentPage);
    } catch {
      message.error("Operation failed");
    }
  };
  const handleSaveOrder = async () => {
    try {
      const source = orderedData.length ? orderedData : machines;

      const reorderedItems = source.map((item, index) => ({
        id: item.id,
        priority: index,
      }));

      await updateMachineryPriority({
        reorderedItems,
      });

      message.success("Machinery order updated successfully");

      setOrderedData([]);
    } catch {
      message.error("Failed to update order");
    }
  };
  // Delete

  const handleDelete = async (id) => {
    await deleteMachinery(id);

    message.success("Deleted");

    fetchMachines(currentPage);
  };

  // Image update

  const handleImageChange = async (file, id) => {
    const reader = new FileReader();

    reader.onload = async () => {
      await updateMachineryImage(id, {
        machineImage: reader.result,
      });

      message.success("Image updated");

      fetchMachines(currentPage);
    };

    reader.readAsDataURL(file);
  };

  // Publish

  const handlePublish = async (id, checked) => {
    await updatePublishStatus(id, {
      isPublish: checked,
    });

    message.success(
      checked ? "Published Successfully" : "Unpublished Successfully",
    );

    fetchMachines(currentPage);
  };

  // Table

  const columns = [
    {
      title: "Image",
      dataIndex: "machineImage",
      render: (img, record) => (
        <Space direction="vertical">
          <Image
            src={img}
            width={60}
            height={60}
            style={{ objectFit: "cover", borderRadius: 6 ,cursor: "pointer"}}
            preview={false}
             onClick={() => {
              setPreviewImage(record.machineImage);
              setPreviewOpen(true);
            }}
          />

          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              handleImageChange(file, record.id);
              return false;
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>
              Update
            </Button>
          </Upload>
        </Space>
      ),
    },

    {
  title: "Machine Title",
  dataIndex: "machineTitle",
},

    {
      title: "Sub Title",
      dataIndex: "machineSubTitle",
    },
{
  title: "Key 1 Title",
  dataIndex: "key1Title",
},
    {
      title: "Key 1 Value",
      dataIndex: "key1Value",
    },
{
  title: "Key 2 Title",
  dataIndex: "key2Title",
},
    {
      title: "Key 2 Value",
      dataIndex: "key2Value",
    },
    {
      title: "Key 3 Title",
      dataIndex: "key3Title",
    },
    {
      title: "Key 3 Value",
      dataIndex: "key3Value",
    },

    {
      title: "Publish",
      render: (_, record) => (
        <Switch
          checked={record.isPublish}
          onChange={(checked) => handlePublish(record.id, checked)}
        />
      ),
    },

    {
      title: "Actions",
      render: (_, record) => (
        <Space direction="vertical">
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingMachine(record);

              setIsModalVisible(true);

             form.setFieldsValue({
  machineTitle: record.machineTitle, 
  machineSubTitle: record.machineSubTitle,
  key1Title: record.key1Title,
  key1Value: record.key1Value,
  key2Title: record.key2Title,
  key2Value: record.key2Value,
  key3Title: record.key3Title,
  key3Value: record.key3Value,
});
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete this machinery?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="admin-layout">
      <Content className="admin-content">
        <Card title={<Title level={4}>Machinery Inventory</Title>}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingMachine(null);

              setImageBase64("");

              setFileList([]);

              form.resetFields();

              setIsModalVisible(true);
            }}
          >
            Add Machine
          </Button>

          <Button
            style={{ marginLeft: 20 }}
            icon={<DragOutlined />}
            onClick={async () => {
              if (!isOrderView) {
                // Fetch all machines when reorder opens
                const res = await getMachineries(1, 1000);

                const allMachines = res?.data?.data || [];

                setMachines(allMachines);

                setOrderedData(
                  allMachines.map((i) => ({
                    id: i.id,
                    image: i.machineImage,
                    title: i.machineTitle,
                    subtitle: i.machineSubTitle,
                  })),
                );
              } else {
                // back to paginated list
                fetchMachines(currentPage);
              }

              setIsOrderView(!isOrderView);
            }}
          >
            {isOrderView ? "Back to List" : "Reorder"}
          </Button>
          {isOrderView && (
            <Button
              type="primary"
              style={{ marginLeft: 10 }}
              onClick={handleSaveOrder}
            >
              Save Order
            </Button>
          )}
          <br />
          <br />

          {isOrderView && (
            <ImageGridSorter
              data={machines.map((i) => ({
                id: i.id,
                image: i.machineImage,
                title: i.machineTitle,
                subtitle: i.machineSubTitle,
              }))}
              onChange={(newOrder) => {
                setOrderedData(newOrder);

                const updatedMachines = newOrder.map((item) =>
                  machines.find((m) => m.id === item.id),
                );

                setMachines(updatedMachines);
              }}
            />
          )}

          {!isOrderView && (
            <Table
              columns={columns}
              dataSource={machines}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize,
                total: totalItems,
                showSizeChanger: false,
                onChange: (page) => {
                  setCurrentPage(page);
                  fetchMachines(page);
                },
              }}
            />
          )}
        </Card>
      </Content>

      {/* Model */}

      <Modal
        open={isModalVisible}
        footer={null}
        title={editingMachine ? "Update Machine" : "Add Machine"}
        onCancel={() => {
          setIsModalVisible(false);

          setEditingMachine(null);

          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
         <Form.Item
  name="machineTitle"
  label="Machine Title"
  rules={[{ required: true }]}
>
  <Input />
</Form.Item>
          <Form.Item
            name="machineSubTitle"
            label="Machine Sub Title"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          {MACHINE_DETAIL_FIELDS.map((field) => (
            <Form.Item
              key={field.name}
              name={field.name}
              label={field.label}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          ))}

          {!editingMachine && (
            <Form.Item label="Machine Image">
              <Upload
                fileList={fileList}
                maxCount={1}
                beforeUpload={(file) => {
                  const reader = new FileReader();

                  reader.onload = () => {
                    setImageBase64(reader.result);
                  };

                  reader.readAsDataURL(file);

                  setFileList([file]);

                  return false;
                }}
                onRemove={() => {
                  setFileList([]);

                  setImageBase64("");
                }}
              >
                <Button icon={<UploadOutlined />}>Upload Image</Button>
              </Upload>
            </Form.Item>
          )}

          {!editingMachine && (
            <Form.Item name="isPublish" label="Publish" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <Button type="primary" htmlType="submit" block>
            {editingMachine ? "Update Machine" : "Add Machine"}
          </Button>
        </Form>
      </Modal>
         {/* image preview */}
      
              <Modal
                open={previewOpen}
                footer={null}
                onCancel={() => setPreviewOpen(false)}
              >
                <Image src={previewImage} />
              </Modal>
    </Layout>
  );
};

export default Machineries;
